import {useMutation} from '@apollo/react-hooks';

import type {
    McfxCompaniesUnlock as McfxCompaniesUnlockMutation,
    McfxCompaniesUnlockVariables as McfxCompaniesUnlockMutationVariables,
    McfxCompaniesUnlockInput,
    GetUnlockInfo as GetUnlockInfoQuery,
} from 'nutshell-graphql-types';

import GET_UNLOCK_INFO from '../../../../people-iq/graphql/queries/get-unlock-info.graphql';

import MCFX_COMPANY_FRAGMENT from '../../graphql/fragments/mcfx-company-fragment.graphql';
import MCFX_COMPANIES_UNLOCK from '../../graphql/mutations/unlock-companies.graphql';

export function useUnlockCompanies(): (input: McfxCompaniesUnlockInput) => Promise<any> {
    const [unlockCompanies] = useMutation<
        McfxCompaniesUnlockMutation,
        McfxCompaniesUnlockMutationVariables
    >(MCFX_COMPANIES_UNLOCK);

    return (input) => {
        return unlockCompanies({
            variables: {input},
            update: (proxy, mutationResults) => {
                // Keep the unlock count data up-to-date
                const cacheResult: GetUnlockInfoQuery | null | undefined = proxy.readQuery({
                    query: GET_UNLOCK_INFO,
                });

                if (!cacheResult || !cacheResult.unlockInfo) {
                    return;
                }

                proxy.writeQuery({
                    query: GET_UNLOCK_INFO,
                    data: {
                        unlockInfo: {
                            ...cacheResult.unlockInfo,
                            unlockedThisMonth:
                                cacheResult.unlockInfo.unlockedThisMonth +
                                input.mcfxCompanyIds.length,
                        },
                    },
                });

                // If unlocking one company from the sidebar, update the cache so the display is up-to-date
                if (input.mcfxCompanyIds.length === 1) {
                    const companyId = input.mcfxCompanyIds[0];

                    // Read the data from our cache for this fragment.
                    const companyFragment = proxy.readFragment({
                        // @ts-expect-error
                        id: companyId,
                        fragmentName: 'McfxCompanyFragment',
                        fragment: MCFX_COMPANY_FRAGMENT,
                    });

                    if (companyFragment) {
                        const updatedCompany =
                            mutationResults &&
                            mutationResults.data &&
                            mutationResults.data.mcfxCompaniesUnlock &&
                            mutationResults.data.mcfxCompaniesUnlock.mcfxCompanies[0];

                        if (!updatedCompany) return;

                        proxy.writeFragment({
                            // @ts-expect-error
                            id: companyId,
                            fragmentName: 'McfxCompanyFragment',
                            fragment: MCFX_COMPANY_FRAGMENT,
                            data: updatedCompany,
                        });
                    }
                }
            },
        });
    };
}
